import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Tour, TourProps } from 'antd';
import { useNavigate, useLocation, matchRoutes } from 'react-router-dom';

import { VirtualTourProvider } from 'shared/ui/VirtualTour/VirtualTourProvider';
import getEnvConfig from 'utils/config/env';

import { useUpdateUserAdditionalFieldsMutation } from '../../../features/user/services';
import { useProfileData } from '../../../hooks/useProfileData';

export const VirtualTour: FC = () => {
    const [open, setOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const communitiesRef = useRef(null);
    const communitiesOverviewRef = useRef(null);
    const communitiesScenariosRef = useRef(null);
    const communitiesMembersRef = useRef(null);
    const communitiesEventsRef = useRef(null);
    const communitiesKnowledgeRef = useRef(null);
    const communitiesMonetizationRef = useRef(null);
    const communitiesAnalyticsRef = useRef(null);
    const profileRef = useRef(null);
    const feedbackRef = useRef(null);

    VirtualTourProvider.set({
        communitiesRef,
        communitiesOverviewRef,
        communitiesScenariosRef,
        communitiesMembersRef,
        communitiesEventsRef,
        communitiesKnowledgeRef,
        communitiesMonetizationRef,
        communitiesAnalyticsRef,
        profileRef,
        feedbackRef,
    });
    const { user } = useProfileData();

    const [updateUserAdditionalFields] =
        useUpdateUserAdditionalFieldsMutation();

    useEffect(() => {
        if (
            user &&
            !user.is_see_tour &&
            pathname.includes('/communities/chats')
        ) {
            setTimeout(() => {
                setOpen(true);
            }, 500);
        }
    }, [user, pathname]);

    const stepsNavigate = [
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities/chats`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities/rules`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities/members`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities/events`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/communities/knowledge`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/monetization/donate`),
        () => navigate(`${getEnvConfig().PUBLIC_PATH}/analitycs`),
        () => {},
        () => {},
    ];
    const steps: TourProps['steps'] = [
        {
            title: 'Сообщества',
            description:
                'Подключите свои сообщества к Spodial чтобы получить полезные инструменты для модерации, аналитики и монетизации.',
            target: () => communitiesRef.current,
        },
        {
            title: 'Обзор',
            description:
                'Все сообщества которые вы подключили к Spodial доступны в этом разделе.',
            target: () => communitiesOverviewRef.current,
        },
        {
            title: 'Сценарии',
            description:
                'Установите правила по которым должны функционировать ваши сообщества и настройте действия за их нарушения.',
            target: () => communitiesScenariosRef.current,
        },
        {
            title: 'Участники',
            description:
                'Контролируйте состав участников вашего сообщества и корректируйте его при необходимости.',
            target: () => communitiesMembersRef.current,
        },
        {
            title: 'События',
            description: 'Журнал действий участников в ваших сообществах.',
            target: () => communitiesEventsRef.current,
        },
        {
            title: 'База знаний',
            description:
                'Собирайте и публикуйте важную информацию для ваших сообществ в ручном и автоматическом режиме на основе общения участников.',
            target: () => communitiesKnowledgeRef.current,
        },
        {
            title: 'Монетизация',
            description:
                'Используйте Spodial для создания платного доступа в ваши сообщества и сбора донатов.',
            target: () => communitiesMonetizationRef.current,
        },
        {
            title: 'Аналитика',
            description:
                'Максимум информации о работе ваших сообществ наглядно представленный для удобного изучения.',
            target: () => communitiesAnalyticsRef.current,
        },
        {
            title: 'Профиль',
            description:
                'Подключить расширенные возможности системы, поменять пароль, номер телефона и привязать Telegram аккаунты.',
            target: () => profileRef.current,
        },
        {
            title: 'Обратная связь',
            description:
                'Если у вас есть вопросы по работе с системой или предложения о сотрудничестве — сообщите нам. Ответ придёт на вашу электронную почту.',
            target: () => feedbackRef.current,
        },
    ];
    const onFinishTour = async () => {
        await updateUserAdditionalFields({ is_see_tour: true });
    };
    return (
        <Fragment>
            <Tour
                zIndex={1051}
                open={open}
                onClose={() => {
                    onFinishTour().then(() => setOpen(false));
                }}
                steps={steps}
                onChange={current => {
                    stepsNavigate[current]();
                    if (current === steps.length - 1) {
                        onFinishTour();
                    }
                }}
            />
        </Fragment>
    );
};
